import { Asset } from "expo-asset";
import { EncodingType, readAsStringAsync } from "expo-file-system/legacy";
import { shareAsync } from "expo-sharing";
import isNil from "lodash/isNil";

import logoImage from "@/assets/images/equip-logo-horizontal.png";
import { printToFileAsync } from "@/crossPlatform/expo-print";
import { isWeb } from "@/utils/platform.util";

// on web, expo-file-system's readAsStringAsync is unavailable, so read the
// resolved asset URL into a base64 payload via the browser's FileReader
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise<string>((resolve) => {
    const reader = new globalThis.FileReader();
    reader.onloadend = () => {
      // reader.result is a data URL ("data:image/png;base64,<payload>");
      const result = typeof reader.result === "string" ? reader.result : "";
      // strip the prefix since getSafetyPlanHTML supplies its own
      resolve(result.split(",")[1] ?? "");
    };
    // on read failure, degrade to no logo rather than rejecting
    reader.onerror = () => resolve("");
    reader.readAsDataURL(blob);
  });

const getBase64Image = async () => {
  try {
    const asset = Asset.fromModule(logoImage);
    await asset.downloadAsync();

    if (isWeb()) {
      const response = await globalThis.fetch(asset.uri);
      return await blobToBase64(await response.blob());
    }

    if (asset.localUri) {
      const base64 = await readAsStringAsync(asset.localUri, {
        encoding: EncodingType.Base64,
      });
      return base64;
    }
    return "";
  } catch {
    return "";
  }
};

/* eslint max-lines-per-function: ["error", {max: 300}] -- Due to long html strings */
/* eslint complexity: ["error", {max: 30}] -- Due to long html strings */
/* eslint max-lines: ["error", {max: 350}] -- Due to long html strings */
const getSafetyPlanHTML = async ({
  completedOn,
  safetyPlan,
  title,
}: {
  completedOn: string;
  safetyPlan: SafetyPlan;
  title: string;
}) => {
  const base64Image = await getBase64Image();

  const {
    copingSkills: copingSkillsData,
    imminentRiskPlan,
    localEmsPhone,
    otherWarningSigns,
    reasonToLive,
    supportPeople,
    warningSigns: warningSignsData,
  } = safetyPlan;

  const warningSigns = isNil(warningSignsData)
    ? []
    : Object.values(warningSignsData);
  const copingSkills = isNil(copingSkillsData)
    ? []
    : copingSkillsData.map(
        (copingSkill: SafetyPlanCopingSkill) =>
          `${copingSkill.Skill} - ${copingSkill.Description}`,
      );
  return ` <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no" />
        <style>
          @media print {
            @page {
              margin-top: 40px !important;
              margin-bottom: 40px !important;
              size: auto;
            }

            body {
              margin-top: 40px !important;
              margin-bottom: 40px !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
              padding: 40px !important;
              max-width: none !important; /* Remove max-width constraint for print */
            }

            /* Force title and content to stay together */
            .title {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              display: inline-block !important;
            }

            /* Keep additional info section together */
            .title + .section {
              page-break-before: avoid !important;
              break-before: avoid !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              display: inline-block !important;
              width: 100%;
            }

            .info-section {
              margin-top: 24px;
              page-break-before: avoid !important;
              break-before: avoid !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              display: inline-block !important;
              width: 100%;
            }

            /* Ensure sections don't break awkwardly */
            .section {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }

            .header {
                display: flex;
                flex-direction: column;
                width: 100%;
                margin-bottom: 36px;
            }
            .logo {
                width: 120px;
                height: auto;
                margin-bottom: 24px;
            }
            .title {
                font-size: 24px;
                font-weight: bold;
            }
            .title-container {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
            }
            .completed-on {
                font-size: 12px;
                color: #666666;
                text-align: right;
            }
            .section {
                display: flex;
                margin-bottom: 24px;
                break-inside: avoid;
                page-break-inside: avoid;
            }
            .section-title {
                flex: 0 0 200px;
                font-size: 12px;
                color: #666666;
                padding-right: 20px;
            }
            .section-content {
                flex: 1;
                min-width: 0;
            }
            .bullet-point {
                margin-bottom: 8px;
            }
            .link {
                color: #007F79;
                text-decoration: none;
            }
            .subsection {
                margin-bottom: 20px;
            }
            .subsection-title {
                font-size: 12px;
                color: #007F79;
                margin-bottom: 8px;
            }
        }
        </style>
      </head>
      <body>
        <div class="header">
            <img src="data:image/png;base64,${base64Image}" class="logo" />
            <div class="title-container">
                <div class="title">${title}</div>
                <div class="completed-on">${completedOn}</div>
            </div>
        </div>

        <div class="section">
          <div class="section-title">Local EMS:</div>
          <div class="section-content">
            ${localEmsPhone && `<a href="tel:${localEmsPhone}" class="link">${localEmsPhone}</a>`}
          </div>
        </div>

        <div class="section">
          <div class="section-title">List three warning signs that you are in a crisis</div>
          <div class="section-content">
            ${
              warningSigns.length > 0
                ? warningSigns
                    .map((sign) => `<div class="bullet-point">• ${sign}</div>`)
                    .join("")
                : ""
            }
          </div>
        </div>

        <div class="section">
          <div class="section-title">Other warning signs</div>
          <div class="section-content">${otherWarningSigns || ""}</div>
        </div>

        <div class="section">
          <div class="section-title">Name at least three internal coping skills</div>
          <div class="section-content">
            ${
              copingSkills
                .filter((skill) => skill && !skill.includes("undefined"))
                .map(
                  (skill: string) =>
                    `<div class="bullet-point">• ${skill}</div>`,
                )
                .join("") || ""
            }
          </div>
        </div>

        <div class="section">
          <div class="section-title">The most important reason to be alive (/recovering)</div>
          <div class="section-content">${reasonToLive || ""}</div>
        </div>

        <div class="section">
          <div class="section-title">Names of at least three support people</div>
          <div class="section-content">
            ${
              supportPeople
                ?.filter((person: SafetyPlanSupportPerson) => person.Name)
                .map(
                  (person: SafetyPlanSupportPerson) => `
                  <div class="bullet-point">• ${person.Name}${
                    person.Phone
                      ? ` - <a href="tel:${person.Phone}" class="link">${person.Phone}</a>`
                      : ""
                  }</div>
                `,
                )
                .join("") || ""
            }
          </div>
        </div>

        <div class="section">
          <div class="section-title">If you are at risk of hurting yourself:</div>
          <div class="section-content">
            <div class="bullet-point">• Call <a href="tel:911" class="link">911</a></div>
            <div class="bullet-point">• Go to the nearest emergency room</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">If I am at imminent risk of hurting myself, my plan is to</div>
          <div class="section-content">${imminentRiskPlan || ""}</div>
        </div>


        <div class="info-section">
        <div class="title">Additional helpful information</div>
        <div class="section" style="margin-top: 24px;">
          <div class="section-content">
            <div class="section">
              <div class="section-title">Hotlines</div>
              <div class="section-content">
                <div class="bullet-point">• National Suicide & Crisis Lifeline: Dial <a href="tel:988" class="link">988</a></div>
                <div class="bullet-point">• National Domestic Violence Hotline: <a href="tel:1-800-799-7233" class="link">1-800-799-7233</a></div>
                <div class="bullet-point">• The Trevor Project Hotline: <a href="tel:1-866-488-7386" class="link">1-866-488-7386</a></div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Chat suicide line</div>
              <div class="section-content">
                <a href="https://suicidepreventionlifeline.org/chat" class="link">https://suicidepreventionlifeline.org/chat</a>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Text suicide line</div>
              <div class="section-content">
                Text "HOME" to <a href="sms:741741" class="link">741741</a> to connect with a Crisis Counselor<br/>
                <a href="https://www.crisistextline.org" class="link">https://www.crisistextline.org</a>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Website with skills to use in a crisis</div>
              <div class="section-content">
                <a href="https://nowmattersnow.org" class="link">https://nowmattersnow.org</a>
              </div>
            </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;
};

const shareSafetyPlan = async ({
  completedOn,
  safetyPlan,
  title,
}: {
  completedOn: string;
  safetyPlan: SafetyPlan;
  title: string;
}) => {
  const html = await getSafetyPlanHTML({ completedOn, safetyPlan, title });
  const result = await printToFileAsync({
    html,
  });
  if (isWeb()) return;

  const { uri } = result;
  await shareAsync(uri, {
    dialogTitle: title,
  });
};

export { blobToBase64, shareSafetyPlan };
